import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators'
import { Entity } from '../../../modules/dto'
import { isEqual } from 'lodash'
import { StoredEntityService } from './stored-entity.service'
import { Injectable } from '@angular/core'
import { AuthEventService } from '../../../auth/services/auth-event.service'

/**
 *  @export
 * @class StoredEntityStateService
 * 
 * Provides a 'selectedEntity' functionality for components. These components can share information regarding that entity, change the selected type or id or inform components, to update their state.
 * 
 * Important: This is designed as a local service, to be provided for a (partial) component-tree by providing it via the parent-component of such a tree:
 
 @Component({ //...
  providers: [StoredEntityStateService]
})

 * open items:
 * can this approach cover all requirements regarding multiple instances of this service on one 'page'? 
 */
@Injectable()
export class StoredEntityStateService {
  /**
   * Currently selected entity.
   * @deprecated entity$ is deprecated. Use StoredEntityService.getEntity$() instead.
   */
  entity$: Observable<Entity>

  /**
   * Id of the currently selected entity.
   * @deprecated entityId$ is deprecated. Use StoredEntityService.getEntity$() instead and extract the id.
   */
  entityId$: Observable<number>

  /**
   * Type of the currently selected entity.
   * @deprecated entityType$ is deprecated. Use StoredEntityService.getEntity$() instead and extract the type.
   */
  entityType$: Observable<string>

  // stores the selected entity-id and -type
  private _$selectedIdAndType: ReplaySubject<[number, string]> = new ReplaySubject<[number, string]>(1)

  // since we need distinctUntilChanged we'll create a private observable from a private subject.
  private _selectedIdAndType$: Observable<[number, string]> = this._$selectedIdAndType
    .asObservable()
    .pipe(distinctUntilChanged())

  // sends signal to loosely connected components, informing them, that something regarding that entity, has happened.
  // this is somewhat crude and could be refined, when the types of use are all identified.
  private _$entityUpdated: Subject<void> = new Subject<void>()

  /** Gets signal when an event on the entity happened (directly or indirectly (like a file was attached)), which can be used to react to such an event.*/
  entityUpdated$: Observable<void> = this._$entityUpdated.asObservable()

  constructor(
    private storedEntityService: StoredEntityService,
    authEventService: AuthEventService
  ) {
    // console.log('instantiating StoredEntityStateService');
    // initialize current version of the selected entity from the selected id and type of the entity
    this.entity$ = this._selectedIdAndType$.pipe(
      // continue only, if id and type are specified.
      filter(([id, type]) => id > 0 && !!type),
      // using id and type to get entity from store
      switchMap(([id, type]) => storedEntityService.getEntity$(type, id)),
      // assure that no update for same entity is fired (todo: check if selector alone avoids that anyway.)
      distinctUntilChanged((a, b) => isEqual(a, b)),
      tap(() => this._$entityUpdated.next())
    )
    this.entityId$ = this._selectedIdAndType$.pipe(map(([id, _]) => id))
    this.entityType$ = this._selectedIdAndType$.pipe(map(([_, type]) => type))
    authEventService.logoutEvent$.subscribe(() => this.reset())
  }

  /** setting the selected entity with type and id.
   * returns the instance to the entity.
   */
  setEntityWithTypeAndId$(type: string, id: number): Observable<Entity> {
    // console.log('setEntityWithTypeAndId', type, id);
    this.setEntityWithTypeAndId(type, id)
    return this.entity$
  }

  /** setting the selected entity with type and id. */
  setEntityWithTypeAndId(type: string, id: number): void {
    // console.log('setEntityWithTypeAndId', type, id);
    this._$selectedIdAndType.next([id, type])
  }

  /** triggers a signal to inform related components about an event on the entity. */
  /*thoughts: 
  - this should not be publicly available. but for the moment working with interfaces/inheritance would feel like overkill.
  */
  triggerUpdateSignal(): void {
    // console.log('triggering update signal...');
    combineLatest([this.entityType$, this.entityId$])
      .pipe(
        take(1),
        tap(([type, id]) => console.log('triggering for: ', type, id))
      )
      .subscribe()
    this._$entityUpdated.next()
  }

  forceRefresh(): void {
    this._selectedIdAndType$
      .pipe(
        take(1),
        switchMap(([id, type]) => this.storedEntityService.getEntity$(type, id, { enforceFreshBackendRequest: true }))
      )
      .subscribe()
  }

  reset(): void {
    this._$selectedIdAndType.next([0, null])
  }
}
